// Prompt user nhập Personal Access Token cho non-GitHub git host (GitLab, Bitbucket,
// Gitea, v.v.) và lưu vào system keychain qua `git credential approve`.
//
// Là single source of truth — dùng chung cho cả clone-repo recovery menu VÀ
// init-flow recovery handler (DRY). Caller chỉ cần await + check boolean trả về.
//
// Lưu ý bảo mật: token KHÔNG được log ra stdout. input() của @inquirer/prompts
// không có mask — user cần nhập cẩn thận. Token chỉ đi thẳng vào git credential store.

import { getActiveFlag } from "./non-interactive-context.js";
import { inputOrFlag } from "./prompt-or-flag.js";
import { readSecretIfRequested } from "./read-secret-from-stdin.js";
import { storeGitCredentialForHost } from "./store-git-credential-for-host.js";
import { log } from "./terminal-logger.js";

/**
 * Hiển thị hướng dẫn, prompt username + PAT, lưu vào keychain.
 * Return true nếu credential đã lưu thành công (caller nên re-verify remote).
 * Return false nếu store thất bại (user có thể thử lại hoặc abort).
 */
const ADD = "add repo";
const PAT_USER_FLAG_SPEC = { flag: "--pat-user", command: ADD, hint: "--pat-user <username>" };
const PAT_STDIN_FLAG_SPEC = {
  flag: "--pat-stdin",
  command: ADD,
  hint: 'echo "$PAT" | avatar add repo --non-interactive --pat-stdin …',
};

export async function promptAndStorePatForHost(host: string): Promise<boolean> {
  // Hướng dẫn theo host — GitLab có URL chuẩn để tạo PAT
  const tokenPageUrl = `https://${host}/-/user_settings/personal_access_tokens`;
  log.info(`Cần Personal Access Token (PAT) để truy cập ${host}.`);
  log.dim(`  Tạo PAT tại: ${tokenPageUrl}`);
  log.dim(`  Scope tối thiểu: read_repository (thêm write_repository nếu cần push).`);
  log.dim(`  Lưu ý: "password" git yêu cầu là PAT — KHÔNG phải mật khẩu web login.`);

  const username = await inputOrFlag(getActiveFlag("patUser"), PAT_USER_FLAG_SPEC, {
    message: `Username trên ${host} (thường là email hoặc account handle):`,
    validate: (v) => (v.trim().length > 0 ? true : "Username bắt buộc"),
  });

  const token = await inputOrFlag(
    await readSecretIfRequested(getActiveFlag<boolean>("patStdin")),
    PAT_STDIN_FLAG_SPEC,
    {
      message: `Personal Access Token (PAT) cho ${host}:`,
      validate: (v) => (v.trim().length > 0 ? true : "Token bắt buộc"),
    },
  );

  const result = storeGitCredentialForHost({
    host,
    username: username.trim(),
    token: token.trim(),
  });

  if (result.ok) {
    log.success(`Credential đã lưu vào keychain cho ${host}. Đang verify lại...`);
    return true;
  }

  log.warn(`Lưu credential thất bại: ${result.error ?? "lỗi không xác định"}`);
  log.dim(`  Thử chạy tay: git credential approve, hoặc check git credential helper config.`);
  return false;
}
